import { useCallback, useEffect, useState } from 'react';
import { Icon } from './components/Icon';
import { ToastProvider } from './components/Toast';
import { AuthProvider, useAuth } from './lib/auth';
import { api } from './lib/api';
import { Login } from './Login';
import { Sidebar } from './Sidebar';
import { Dashboard } from './pages/Dashboard';
import { Fila } from './pages/Fila';
import { ContasAPagar } from './pages/ContasAPagar';
import { DetalheConta } from './pages/DetalheConta';
import { NovoLancamento } from './pages/NovoLancamento';
import { Guias } from './pages/Guias';
import { Fornecedores } from './pages/Fornecedores';
import { PlanoContas } from './pages/PlanoContas';
import { ContasBancarias } from './pages/ContasBancarias';
import { Aportes } from './pages/Aportes';
import { Relatorios } from './pages/Relatorios';
import { Extrato } from './pages/Extrato';
import { Configuracoes } from './pages/Configuracoes';

export interface ContasFilters {
  status: 'todos' | 'pendente' | 'aguardando' | 'pago' | 'atrasadas' | 'a_vencer';
  search: string;
  perDe: string;
  perAte: string;
  semNf: boolean;
}

export const DEFAULT_CONTAS_FILTERS: ContasFilters = { status: 'a_vencer', search: '', perDe: '', perAte: '', semNf: false };

export interface PageProps {
  navigate: (id: string, params?: Record<string, unknown>) => void;
  params: Record<string, unknown>;
  reloadFila: () => void;
  contasFilters: ContasFilters;
  setContasFilters: (f: ContasFilters) => void;
}

const PAGES: Record<string, (props: PageProps) => JSX.Element> = {
  dashboard: Dashboard,
  fila: Fila,
  contas: ContasAPagar,
  detalhe: DetalheConta,
  novo: NovoLancamento,
  guias: Guias,
  fornecedores: Fornecedores,
  plano: PlanoContas,
  bancos: ContasBancarias,
  aportes: Aportes,
  relatorios: Relatorios,
  extrato: Extrato,
  config: Configuracoes,
};

function AppInner() {
  const { user, loading } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [params, setParams] = useState<Record<string, unknown>>({});
  const [navOpen, setNavOpen] = useState(false);
  const [filaCount, setFilaCount] = useState(0);
  const [contasFilters, setContasFilters] = useState<ContasFilters>(DEFAULT_CONTAS_FILTERS);

  const reloadFila = useCallback(() => {
    if (!user) return;
    Promise.all([
      api.listContas({ classificacao_status: 'sugerida', limit: 1 }).then((r) => r.total).catch(() => 0),
      api.listComprovantes({ match_status: 'pending', limit: 1 }).then((r) => r.total).catch(() => 0),
    ]).then(([a, b]) => setFilaCount(a + b));
  }, [user]);

  useEffect(() => { reloadFila(); }, [reloadFila]);

  const navigate = (id: string, p: Record<string, unknown> = {}) => {
    // Vindo do dashboard com um status, aplica ao filtro persistente das contas.
    if (id === 'contas' && p.sem_nf === true) {
      setContasFilters({ ...DEFAULT_CONTAS_FILTERS, status: 'todos', semNf: true });
    } else if (id === 'contas' && typeof p.status === 'string') {
      setContasFilters({ ...DEFAULT_CONTAS_FILTERS, status: p.status as ContasFilters['status'] });
    }
    setPage(id);
    setParams(p);
    setNavOpen(false);
    window.scrollTo(0, 0);
  };

  if (loading) {
    return <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', color: 'var(--muted)' }}>Carregando…</div>;
  }
  if (!user) return <Login />;

  const Page = PAGES[page] ?? Dashboard;
  const key = page + (params.id ? `:${params.id}` : '');

  return (
    <ToastProvider>
      <div className="app">
        <Sidebar page={page} navigate={navigate} open={navOpen} onClose={() => setNavOpen(false)} filaCount={filaCount} />
        <div className="main">
          <button className="btn ghost icon mobile-toggle" style={{ position: 'fixed', top: 12, left: 12, zIndex: 70 }} onClick={() => setNavOpen((o) => !o)}>
            <Icon name="menu" size={18} />
          </button>
          <main className="content">
            <Page key={key} navigate={navigate} params={params} reloadFila={reloadFila}
              contasFilters={contasFilters} setContasFilters={setContasFilters} />
          </main>
        </div>
      </div>
    </ToastProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
